<template>
  <div class="pa-4">
    <v-row justify="space-between">
      <v-col v-if="!noHeader">
        <div class="font-weight-bold basic--text text-center">{{ 'Qualification Details'.toUpperCase() }}</div>
      </v-col>
    </v-row>
    <v-form ref="form" v-model="dataValid">
      <v-alert type="warning" class="mt-4">
        Make sure to provide correct information as editing will not be allowed after submission
      </v-alert>
      <v-alert type="warning" class="mt-4">
        Transfer students are to select the Transcript option and provide NIL in other fields
      </v-alert>
      <div class="d-flex">
        <div class="basic--text font-weight-bold">Number of qualifications</div>
        <div class="ml-4">
          <v-select :items="qualificationsCounts" dense outlined :disabled="!canEdit" v-model="qno"
            label="Number of qualifications"></v-select>
        </div>
      </div>
      <v-row v-for="index in qno" :key="index">
        <v-col cols="12" md="4">
          <div>
            <v-select dense :placeholder="'Qualification ' + index" outlined :items="qualifications" item-text="title"
              item-value="id" :disabled="!canEdit"
              v-model="results[index - 1].qualificationId" required :rules="textRules"></v-select>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div>
            <v-text-field dense placeholder="Course" outlined
              :disabled="!canEdit"
              v-model="results[index - 1].courseOfStudy" required :rules="textRules"></v-text-field>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div>
              <v-text-field dense placeholder="Institution" outlined
              :disabled="!canEdit"
              v-model="results[index - 1].school" required :rules="textRules"></v-text-field>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div>
            <v-select dense placeholder="Year" outlined :items="years"
              :disabled="!canEdit" v-model="results[index - 1].year" required
              :rules="textRules"></v-select>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div>
            <v-select dense placeholder="Grade" outlined :items="grades" item-value="id" item-text="title"
              :disabled="!canEdit"
              v-model="results[index - 1].gradeId" required :rules="textRules"></v-select>
          </div>
        </v-col>
        <v-col cols="12" md="4" v-if="documentRequired">
          <div>
            <v-file-input
                label="Select document"
                required
                :rules="documentRules"
                dense
                outlined
                v-model="files[index - 1]"
                accept="application/pdf"
                show-size
                hint="Maximum file size: 500 KB"
                persistent-hint
            ></v-file-input>
          </div>
        </v-col>
      </v-row>
      <v-card-actions class="mt-6">
        <v-spacer></v-spacer>
        <v-btn color="basic" class="white--text" @click="$emit('back')" depressed>{{  noHeader ? 'Back' : 'Previous' }}</v-btn>
        <v-btn color="error" depressed @click="$refs.form.reset()" v-if="userData.length === 0 && !noHeader" :disabled="!canEdit">
          Reset</v-btn>
        <v-btn color="primary" depressed @click="saveData" :disabled="!dataValid || !qno">{{  noHeader ? 'Save' : 'Proceed' }}</v-btn>
        <v-btn color="info" depressed @click="success" v-if="userData.length !== 0 || !canEdit">Skip</v-btn>
      </v-card-actions>
    </v-form>
    <loading-dialog :show="loading"></loading-dialog>
    <message-box :show="messageShow" :message="message" :type="messageType"
      v-on:close-message="messageShow = !messageShow"></message-box>
  </div>
</template>

<script>

import ValidationRules from "@/shared/ValidationRules";
import DateUtil from "@/shared/DateUtil";
import { mapGetters } from "vuex";
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";
import StudentStatus from "@/shared/StudentStatus";
import {
  APPLICATION_DOCUMENT_SIZE_ERROR,
  applicationDocumentSizeRule,
  isApplicationDocumentSizeAllowed,
} from "@/application/applicationDocumentValidation";

export default {
  name: "Qualifications",
  props: {
    noHeader: Boolean
  },
  components: { MessageBox, LoadingDialog },
  data: () => ({
    textRules: ValidationRules.textRules,
    documentRules: [...ValidationRules.textRules, applicationDocumentSizeRule],
    numberRules: ValidationRules.numericRules,
    userData: [],
    dataValid: true,
    messageType: '',
    message: '',
    messageShow: false,
    loading: false,
    years: DateUtil.getYearsArray(1900),
    obj: {},
    files: [],
    numberOfQualifications: 0,
    results: [],
    institutions: [],
    grades: [],
    qualifications: [],
    qualificationsCounts: [],
    qno: 0,
  }),


  methods: {
    success() {
      this.$emit('success')
    },

    fillQualificationsWithDefaults() {
      if (this.results.length > 0) {
        for (let i = 0; i < 10; i++) {
          this.results[i] = {}
          this.files[i] = null
        }
      } else {
        for (let i = 0; i < 10; i++) {
          this.results.push({})
          this.files.push()
        }
      }
    },

    check() {
      this.loading = true;
      Api.applicants.nextToQualificationUpdate(this.getUser.id)
        .then(res => {
          this.loading = false;
          this.$store.commit("setCurrentUser", res.data)
        }).catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false)
    },

    saveData() {
      const hasOversizedDocument = this.files
        .slice(0, this.qno)
        .some(file => file && !isApplicationDocumentSizeAllowed(file))
      if (hasOversizedDocument) {
        this.message = APPLICATION_DOCUMENT_SIZE_ERROR
        this.messageType = 'error'
        this.messageShow = true
        return
      }

      let fd = new FormData()
      // copy required results
      let d = []
      for (let i = 0; i < this.qno; i++)
        d.push(this.results[i])

      const json = JSON.stringify(d);
      // append results
      fd.append("results", json)

      // add files
      for (let i = 0; i < this.qno; i++) {
        if (this.files[i]) fd.append("files", this.files[i])
      }

      this.loading = true
      Api.applicants.qualificationUpdate(this.getUser.id, fd)
        .then(res => {
          this.message = 'Qualification Details updated successfully'
          this.messageType = 'success'
          this.messageShow = true
          this.$store.commit("setQualificationDetails", res.data)
          this.success()
        }).catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false)
    },

    loadBasicDetails() {
      this.loading = true
      Api.requirements.getQualificationDetails()
        .then(res => {
          this.grades = res.data.grades
          this.qualifications = res.data.qualifications
        })
        .catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false)
    },

    loadQualificationDetails() {
      this.loading = true
      Api.applicants.getQualificationDetails(this.getUser.userId)
        .then(res => {
          this.userData = res.data
          this.prefillData()
        })
        .catch(err => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false)
    },

    prefillData() {
      if (this.userData.length > 0) {
        this.qno = this.userData.length
        for (let i = 0; i < this.userData.length; i++) {
          let u = this.userData[i]
          this.results[i].year = +u.year
          if (u.qualification != null)
            this.results[i].qualificationId = u.qualification.id
          if (u.qualificationGrade != null)
            this.results[i].gradeId = u.qualificationGrade.id

          this.results[i].courseOfStudy = u.courseOfStudy
          this.results[i].school = u.school
        }
      }
    }
  },

  computed: {
    ...mapGetters(['getUser', 'getInformation', 'getQualificationCertificate']),
    canEdit: function(){
      return this.getUser != null &&
        StudentStatus[this.getUser.studentStatus].value <= StudentStatus.SCREENING_REQUIREMENTS_PENDING.value
        && this.getUser.admission.applicationType.qualificationDetailsEnabled && (this.getUser.modeOfEntry == null || this.getUser.modeOfEntry.requireScreeningDocuments)
    },

    documentRequired: function(){
      return this.qualificationDocumentsEnabled
    },

    qualificationDocumentsEnabled: function(){
      return this.getUser != null && this.getUser.admission.applicationType.qualificationDocumentEnabled
    }
  },

  watch: {
    getUser: function (val) {
      if (val != null) {
        if (this.getUser.admission.applicationType.qualificationDetailsEnabled === false || (this.getUser.modeOfEntry != null && !this.getUser.modeOfEntry.requireScreeningDocuments)) {
          this.success()
        }
        this.qualificationsCounts = []
        for (let i = 1; i <= this.getUser.admission.applicationType.numberOfQualifications; i++) {
          this.qualificationsCounts.push(i)
        }
      }
    },

    userData: function (val) {
      if (val != null && val.length > 0) {
        // this.success()
      }
    }
  },

  created() {
    if (this.getUser != null) {
      if (this.getUser.admission.applicationType.qualificationDetailsEnabled === false ||(this.getUser.modeOfEntry != null && !this.getUser.modeOfEntry.requireScreeningDocuments)) {
        this.success()
        return
      }
      this.check()
      for (let i = 1; i <= this.getUser.admission.applicationType.numberOfQualifications; i++) {
        this.qualificationsCounts.push(i)
      }
    }

    this.fillQualificationsWithDefaults()
    this.loadBasicDetails()
    this.loadQualificationDetails()
    this.noHeader && this.$emit('update-title', "Qualification")
  }
}
</script>
